import type { PostVisibility } from '@citizen-walk/shared-types';
import { deletePost, moderateVisibility } from './usePostsModeration';

/**
 * Bulk moderation helper (Sprint BG-DataSettings 21、design system 補完 + admin 一括操作)。
 *
 * 旧 v1 (`old/apps/admin/src/pages/PostsModerationPage.tsx` L275-294) では Cloud Functions
 * `moderatePost` callable を per-id loop で呼んでいた。v2 では `moderatePost` callable が
 * 未実装 (Phase 5 R-20 で予定) のため、minimum scope として既存の client 直接 write
 * (`moderateVisibility` / `deletePost`) を loop で呼ぶ。
 *
 * 旧との差分:
 *   - 踏襲: per-id loop で順次実行、成功 / 失敗を集計、selectedIds.clear で UI reset
 *   - 廃止: client 直接 write のため操作ログは記録されない (R-20 で後置)
 *   - 変更: 集計を return 値 (ok, failed[]) で渡す (旧は inline toast)、UI 側で toast 制御
 */

export type BulkAction = { kind: 'visibility'; visibility: PostVisibility } | { kind: 'delete' };

export interface BulkResult {
  ok: number;
  failed: Array<{ id: string; error: string }>;
}

export interface BulkInput {
  districtId: string;
  postIds: string[];
  action: BulkAction;
  uid: string;
}

export interface BulkRunners {
  moderate?: typeof moderateVisibility;
  remove?: typeof deletePost;
}

/**
 * 一括処理本体 (DI 可能、test 用に runner を差し替えできるよう設計)。
 *
 * - 各 post id ごとに順次 await (並列 N するなら別 sprint で検討、Firestore quota 配慮)
 * - 失敗は集計して返す (1 件失敗で全体停止しない)
 */
export async function runBulkModeration(
  input: BulkInput,
  runners: BulkRunners = {},
): Promise<BulkResult> {
  const moderate = runners.moderate ?? moderateVisibility;
  const remove = runners.remove ?? deletePost;
  const failed: Array<{ id: string; error: string }> = [];
  let ok = 0;
  for (const id of input.postIds) {
    try {
      if (input.action.kind === 'visibility') {
        await moderate(input.districtId, id, input.action.visibility, input.uid);
      } else {
        await remove(input.districtId, id);
      }
      ok++;
    } catch (e) {
      failed.push({ id, error: (e as Error)?.message ?? 'unknown' });
    }
  }
  return { ok, failed };
}

/** 確認 dialog 文言を組み立てる (旧 L277-278 踏襲、UI 側 confirm 直前で呼ぶ)。 */
export function bulkConfirmMessage(count: number, action: BulkAction): string {
  if (action.kind === 'delete') {
    return `${count} 件の投稿を完全に削除します。復元できません。よろしいですか？`;
  }
  const label =
    action.visibility === 'public'
      ? '公開'
      : action.visibility === 'hidden_others'
        ? '本人のみ'
        : '全員非表示';
  return `${count} 件の投稿を「${label}」に変更します。よろしいですか？`;
}
